export const DEFAULT_PORT = 8765
export const QR_PREFIX = 'IWSPOS1:'

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

export interface PairingCode {
    host: string
    port: number
    pin: string
}

type Kind = { tag: number; bits: number; match: (o: number[]) => boolean; pack: (o: number[]) => number; unpack: (v: number) => number[] }

const KINDS: Kind[] = [
    { tag: 0, bits: 16, match: (o) => o[0] === 192 && o[1] === 168, pack: (o) => (o[2] << 8) | o[3], unpack: (v) => [192, 168, v >> 8, v & 255] },
    { tag: 1, bits: 24, match: (o) => o[0] === 10, pack: (o) => (o[1] << 16) | (o[2] << 8) | o[3], unpack: (v) => [10, (v >> 16) & 255, (v >> 8) & 255, v & 255] },
    {
        tag: 2,
        bits: 20,
        match: (o) => o[0] === 172 && o[1] >= 16 && o[1] <= 31,
        pack: (o) => ((o[1] - 16) << 16) | (o[2] << 8) | o[3],
        unpack: (v) => [172, 16 + (v >> 16), (v >> 8) & 255, v & 255],
    },
    { tag: 3, bits: 32, match: () => true, pack: (o) => ((o[0] << 24) | (o[1] << 16) | (o[2] << 8) | o[3]) >>> 0, unpack: (v) => [v >>> 24, (v >>> 16) & 255, (v >>> 8) & 255, v & 255] },
]

const octets = (host: string) => {
    const parts = host.split('.')
    const o = parts.map(Number)
    if (parts.length !== 4 || o.some((n, i) => !/^\d{1,3}$/.test(parts[i]) || n > 255)) throw new Error('Not an IPv4 address')
    return o
}

export const isPrivateIpv4 = (host: string) => {
    try {
        const o = octets(host)
        return KINDS.slice(0, 3).some((k) => k.match(o))
    } catch {
        return false
    }
}

const checkSymbol = (symbols: number[]) => symbols.reduce((sum, s, i) => (sum + (i + 1) * s) % 31, 0)

export const encodePairingCode = ({ host, port, pin }: PairingCode): string => {
    if (!/^\d{4}$/.test(pin)) throw new Error('PIN must be 4 digits')
    if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error('Bad port')
    const o = octets(host)
    const kind = KINDS.find((k) => k.match(o))!
    let bits = ''
    const put = (v: number, n: number) => (bits += v.toString(2).padStart(n, '0'))
    put(kind.tag, 2)
    put(port === DEFAULT_PORT ? 0 : 1, 1)
    put(kind.pack(o), kind.bits)
    if (port !== DEFAULT_PORT) put(port, 16)
    put(Number(pin), 14)
    bits = bits.padEnd(Math.ceil(bits.length / 5) * 5, '0')
    const symbols: number[] = []
    for (let i = 0; i < bits.length; i += 5) symbols.push(parseInt(bits.slice(i, i + 5), 2))
    symbols.push(checkSymbol(symbols))
    const text = symbols.map((s) => ALPHABET[s]).join('')
    return text.length > 4 ? `${text.slice(0, 4)}-${text.slice(4)}` : text
}

const symbolOf = (c: string) => {
    const u = c.toUpperCase().replace('O', '0').replace(/[IL]/, '1')
    const i = ALPHABET.indexOf(u)
    if (i < 0) throw new Error('Invalid character')
    return i
}

export const normalizePairingCode = (input: string) => input.trim().replace(/^IWSPOS1:/i, '').replace(/[\s-]/g, '')

export const decodePairingCode = (input: string): PairingCode => {
    const clean = normalizePairingCode(input)
    if (clean.length < 2) throw new Error('Code too short')
    const symbols = [...clean].map(symbolOf)
    const check = symbols.pop()!
    if (checkSymbol(symbols) !== check) throw new Error('Code mistyped')
    const bits = symbols.map((s) => s.toString(2).padStart(5, '0')).join('')
    let at = 0
    const take = (n: number) => {
        if (at + n > bits.length) throw new Error('Code too short')
        const v = parseInt(bits.slice(at, at + n), 2)
        at += n
        return v
    }
    const kind = KINDS[take(2)]
    const custom = take(1) === 1
    const host = kind.unpack(take(kind.bits)).join('.')
    const port = custom ? take(16) : DEFAULT_PORT
    const pin = take(14)
    const rest = bits.slice(at)
    if (rest.length >= 5 || /1/.test(rest) || pin > 9999 || port === 0) throw new Error('Invalid code')
    return { host, port, pin: String(pin).padStart(4, '0') }
}

export const qrPayload = (code: string) => QR_PREFIX + code
